import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import ImageUploadDropZone from 'packages/kakapo-ui-kit/components/forms/controls/image-upload/ImageUpload';
import Nullable from 'packages/kakapo-ui-services/types/Nullable';
import { ImageFile, IImageSpecification } from 'packages/kakapo-ui-kit/types/Image';
import ImageResizer from 'packages/kakapo-ui-kit/components/forms/controls/image-upload/ImageResizer';
import FormErrorMessage from 'packages/kakapo-ui-kit/components/forms/errors/FormErrorMessage';
import Modal, {
	ModalBackground,
	ModalContent,
} from 'packages/kakapo-ui-kit/components/modals/Modal';
import ModalHeader, {
	ModalHeaderTitle,
} from 'packages/kakapo-ui-kit/components/modals/ModalHeader';
import ResourceError from 'packages/kakapo-ui-services/models/errors/ResourceError';
import PrimaryButton from 'packages/kakapo-ui-kit/components/buttons/PrimaryButton';

interface IProps {
	imageSpecification: IImageSpecification;
	onSubmit: (resizedFileBlob: Nullable<Blob>) => void;
	onCancel: () => void;
}

export default function ImageUploadModalContainer({
	imageSpecification,
	onSubmit,
	onCancel,
}: IProps) {
	const { t } = useTranslation();
	const [uploadedFile, setUploadedFile] = useState<Nullable<ImageFile>>(null);
	const [resizedFileBlob, setResizedFileBlob] = useState<Nullable<Blob>>(null);
	const [error, setError] = useState<Nullable<any>>(null);

	const isSvg = uploadedFile && (uploadedFile as string).includes('data:image/svg+xml');

	return (
		<ModalBackground>
			<Modal>
				<ModalHeader onCancel={onCancel}>
					<ModalHeaderTitle>
						{t('common.personalInfo.images.uploadNew')}
						&nbsp;
						{imageSpecification.title}
					</ModalHeaderTitle>
				</ModalHeader>

				<ModalContent>
					{error && <FormErrorMessage error={error} />}
					{uploadedFile === null && (
						<ImageUploadDropZone
							accept={imageSpecification.allowedExtensions}
							translationPropertyKey={imageSpecification.key}
							onError={(receivedError: Error) => setError(receivedError)}
							onFileSelected={(file: ImageFile) => setUploadedFile(file)}
						/>
					)}
					{uploadedFile !== null && isSvg ? (
						<>
							<img style={{ maxWidth: '100%' }} src={uploadedFile as string} alt="" />
							<PrimaryButton
								onClick={async () => {
									try {
										await onSubmit(new Blob([uploadedFile], { type: 'image/svg+xml' }));
									} catch (e) {
										const resourceError = e as ResourceError;
										const { errorObject } = resourceError;
										if (errorObject !== undefined) {
											const errArr = errorObject![''];
											setError({ message: errArr });
										} else {
											setError(e);
										}
									}
								}}
							>
								Upload
							</PrimaryButton>
						</>
					) : (
						uploadedFile !== null && (
							<ImageResizer
								fileString={uploadedFile as string}
								imageSpecification={imageSpecification}
								onImageResize={(fileBlob: Blob) => setResizedFileBlob(fileBlob)}
								onFinish={async () => {
									try {
										await onSubmit(resizedFileBlob);
									} catch (e) {
										const resourceError = e as ResourceError;
										const { errorObject } = resourceError;
										if (errorObject !== undefined) {
											const errArr = errorObject![''];
											setError({ message: errArr });
										} else {
											setError(e);
										}
									}
								}}
								onError={(receivedError: Error) => setError(receivedError)}
							/>
						)
					)}
				</ModalContent>
			</Modal>
		</ModalBackground>
	);
}
